import {
    count,
    workflowContextProps,
    workflowSearchProps,
    workflowSelectionProps,
} from './order-workflow-contract'
import { workflowErrorCode } from './payment-collection-tracking'
import {
    workflowActions,
    workflowGroups,
    workflowOperations,
    workflowRoute,
    workflowRoutes,
} from './workflow-contract'
import type {
    WorkflowContext,
    WorkflowIdentity,
    WorkflowOptions,
    WorkflowSearch,
    WorkflowSelection,
} from './order-workflow-contract'
import type {
    WorkflowAction,
    WorkflowGroup,
    WorkflowOperation,
    WorkflowOutcome,
    WorkflowType,
} from './workflow-contract'

/** `errorCode` is only kept on `api_error`, sanitized to an upper-snake code. */
export type WorkflowStepMetadata = WorkflowSelection & { errorCode?: string }

const SUGGESTION_USES: ReadonlyArray<unknown> = [
    'picked',
    'picked_edited',
    'typed',
]
const isSuggestionUse = (value: unknown): value is string =>
    SUGGESTION_USES.includes(value)
const isCandidateCount = (value: unknown): value is number =>
    typeof value === 'number' && Number.isInteger(value) && value >= 0

/** Cumulative timing follows the order pilot: gaps over 5s are unobserved effort. */
export function createWorkflowTracker(
    identity: WorkflowIdentity,
    type: WorkflowType,
    options: WorkflowOptions & { operation?: WorkflowOperation } = {},
) {
    const actor = identity.actorId
    const impersonating = identity.impersonating
    const now = options.now ?? (() => performance.now())
    const attemptId = crypto.randomUUID()
    const opened = now()
    let last = opened,
        activity = -Infinity,
        sequence = 0,
        submitIndex = 0
    let foreground = false,
        started = false,
        finished = false
    let foregroundMs = 0,
        activeMs = 0,
        waitMs = 0,
        checkpointAt = opened
    const engagedGroups = new Set<WorkflowGroup>()
    let pending = 0,
        waitingSince = 0
    let context: Record<string, unknown> = {}
    const emit = (event: string, props: Record<string, unknown> = {}) => {
        if (!Object.hasOwn(workflowRoutes, type)) return
        try {
            options.capture?.(event, {
                schema_version: 2,
                workflow_type: type,
                actor,
                impersonating,
                attempt_id: attemptId,
                event_id: crypto.randomUUID(),
                sequence: ++sequence,
                form_variant: 'standard',
                route_template: workflowRoute(type, options.operation),
                operation:
                    options.operation &&
                    workflowOperations.includes(options.operation)
                        ? options.operation
                        : 'create',
                ...context,
                ...props,
            })
        } catch {
            /* Analytics cannot affect the business operation. */
        }
    }
    const observe = () => {
        const time = now(),
            delta = Math.max(0, time - last)
        if (!finished && foreground && delta <= 5000) {
            foregroundMs += delta
            activeMs += Math.max(0, Math.min(time, activity + 30000) - last)
        }
        last = time
    }
    const snapshot = () => {
        observe()
        return {
            elapsed_ms: count(now() - opened),
            foreground_ms: count(foregroundMs),
            interaction_active_ms: count(activeMs),
            api_wait_ms: count(waitMs + (pending ? now() - waitingSince : 0)),
            submit_index: submitIndex,
        }
    }
    const checkpoint = () => {
        if (started && !finished) {
            emit('workflow_checkpoint', snapshot())
            checkpointAt = now()
        }
    }
    return {
        attemptId,
        setContext(metadata: WorkflowContext) {
            if (!finished)
                context = { ...context, ...workflowContextProps(metadata) }
        },
        get finished() {
            return finished
        },
        start() {
            if (!started && !finished) {
                started = true
                emit('workflow_started')
            }
        },
        observe() {
            observe()
            if (foreground && now() - checkpointAt >= 30000) checkpoint()
        },
        setForeground(value: boolean) {
            observe()
            foreground = value
            if (!value) checkpoint()
        },
        interact(group: WorkflowGroup) {
            if (finished || !workflowGroups.includes(group)) return
            observe()
            activity = now()
            if (!engagedGroups.has(group)) {
                engagedGroups.add(group)
                emit('workflow_step', { action: 'engaged', field_group: group })
            }
        },
        search(metadata: WorkflowSearch) {
            if (!finished)
                emit('workflow_search_results', workflowSearchProps(metadata))
        },
        step(action: WorkflowAction, metadata: WorkflowStepMetadata = {}) {
            if (!finished && workflowActions.includes(action))
                emit('workflow_step', {
                    action,
                    submit_index: submitIndex,
                    ...workflowSelectionProps(metadata),
                    ...(action === 'api_error' &&
                    metadata.errorCode !== undefined
                        ? {
                              error_code: workflowErrorCode({
                                  code: metadata.errorCode,
                              }),
                          }
                        : {}),
                })
        },
        submit() {
            if (finished) return () => {}
            submitIndex++
            if (pending++ === 0) waitingSince = now()
            emit('workflow_step', {
                action: 'submit',
                submit_index: submitIndex,
            })
            let settled = false
            return () => {
                if (settled) return
                settled = true
                if (--pending === 0) waitMs += Math.max(0, now() - waitingSince)
            }
        },
        checkpoint,
        /**
         * Only verified reconciliation facts may extend an outcome. Raw reasons,
         * identifiers and arbitrary caller properties never reach analytics.
         */
        finish(
            outcome: WorkflowOutcome,
            recordId?: string,
            properties?: Record<string, unknown>,
        ) {
            if (
                finished ||
                !['api_confirmed', 'closed', 'navigated_away'].includes(outcome)
            )
                return
            const props = snapshot()
            finished = true
            emit('workflow_finished', {
                ...(properties?.matched_by === 'manual'
                    ? { matched_by: 'manual' }
                    : {}),
                ...(typeof properties?.suggested === 'boolean'
                    ? { suggested: properties.suggested }
                    : {}),
                ...(isCandidateCount(properties?.candidate_count)
                    ? { candidate_count: properties.candidate_count }
                    : {}),
                ...(properties?.candidates_status === 'ok' ||
                properties?.candidates_status === 'unavailable'
                    ? { candidates_status: properties.candidates_status }
                    : {}),
                ...(isSuggestionUse(properties?.suggestion_used)
                    ? { suggestion_used: properties.suggestion_used }
                    : {}),
                outcome,
                ...(recordId ? { record_id: recordId } : {}),
                ...props,
            })
        },
    }
}
export type WorkflowTracker = ReturnType<typeof createWorkflowTracker>
